import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Home, Store, MessageSquare, Wallet, Radio, Shield, LogOut, Megaphone, Users, Ticket, UserPlus, Bookmark, Film, Crown, ShieldCheck, Trophy, Moon, Landmark } from "lucide-react";
import { Button } from "./ui/button";
import NotificationBell from "./NotificationBell";
import BrandMark from "./BrandMark";
import BottomNav from "./BottomNav";
import MobileLockupPill from "./MobileLockupPill";
import EmailVerificationBanner from "./EmailVerificationBanner";
import RatePromptDialog from "./RatePromptDialog";
import OnboardingTour from "./OnboardingTour";
import { useRatePrompt } from "../lib/ratePrompt";

export default function Layout({ children, rightRail = null }) {
  const { user, profile, wallet, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const { showWebModal, setShowWebModal } = useRatePrompt();

  const navItems = [
    { to: "/feed", label: "Feed", icon: Home },
    { to: "/friends", label: "Friends", icon: UserPlus },
    { to: "/marketplace", label: "Marketplace", icon: Store },
    { to: "/discover", label: "Discover", icon: ShieldCheck },
    { to: "/leaderboard", label: "Leaderboard", icon: Trophy },
    { to: "/oracle", label: "Oracle", icon: Moon },
    { to: "/temples", label: "Temples", icon: Landmark },
    { to: "/elders", label: "Elders", icon: Crown },
    { to: "/clips", label: "Clips", icon: Film },
    { to: "/livestreams", label: "Livestreams", icon: Ticket },
    { to: "/communities", label: "Communities", icon: Users },
    { to: "/messages", label: "Messages", icon: MessageSquare },
    { to: "/saved", label: "Saved", icon: Bookmark },
    { to: "/wallet", label: "Wallet", icon: Wallet },
    { to: "/referrals", label: "Referrals", icon: Users },
    { to: "/ambassador/dashboard", label: "Ambassador", icon: Trophy },
    { to: "/golive", label: "Go Live", icon: Radio },
  ];
  // Admins are treated as practitioners ("house" subscription).
  if (user?.role === "PROVIDER" || user?.role === "ADMIN") navItems.push({ to: "/ads", label: "Ads", icon: Megaphone });
  if (user?.role === "ADMIN") navItems.push({ to: "/admin", label: "Admin", icon: Shield });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <RatePromptDialog open={showWebModal} onClose={() => setShowWebModal(false)} />
      <EmailVerificationBanner />
      <nav className="cauldron-topnav sticky top-0 z-30 backdrop-blur">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/feed" className="flex items-center gap-3" data-testid="brand-logo">
            <BrandMark size={36} />
            <span className="font-serif text-3xl tracking-wide text-foreground">cauldron</span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-2">
            {profile && profile.subscriptionStatus !== "active" && user?.role === "PROVIDER" && (
              <button onClick={() => nav("/subscribe")} className="hidden sm:inline-flex items-center px-3 py-2 rounded-sm bg-primary text-primary-foreground uppercase tracking-wider text-xs hover:bg-primary/90" data-testid="nav-subscribe-btn">
                Activate
              </button>
            )}
            <button onClick={() => nav("/golive")} className="flex items-center gap-2 px-3 py-2 rounded-sm text-xs uppercase tracking-wider text-foreground/85 hover:text-primary hover:bg-primary/10 transition-colors" data-testid="nav-golive-btn">
              <span className="w-2 h-2 bg-primary rounded-full pulse-ember" />
              <span className="hidden sm:inline">Go Live</span>
            </button>
            {user && <NotificationBell />}
            {user ? (
              <button onClick={() => nav(`/profile/${user.id}`)} className="flex items-center gap-2 group pl-1" data-testid="nav-profile-btn">
                <div className="w-9 h-9 rounded-sm bg-secondary/20 border border-secondary/50 flex items-center justify-center font-serif text-secondary">
                  {user.name?.[0]?.toUpperCase()}
                </div>
                <div className="text-left hidden md:block">
                  <div className="text-xs label-mystic">{user.role}</div>
                  <div className="text-sm">{user.name}</div>
                </div>
              </button>
            ) : null}
            <button onClick={logout} className="text-foreground/70 hover:text-secondary p-2" data-testid="nav-logout-btn">
              <LogOut size={18} strokeWidth={1.25} />
            </button>
          </div>
        </div>

        {/* Mobile / tablet horizontal nav — visible below the top bar on screens < lg */}
        <div className="lg:hidden border-t border-primary/15">
          <div className="max-w-[1600px] mx-auto px-3 py-2 flex gap-1 overflow-x-auto scroll-thin" data-testid="mobile-sidebar-nav">
            {navItems.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className={`flex items-center gap-2 px-3 py-2 rounded-sm whitespace-nowrap text-xs uppercase tracking-wider ${
                  loc.pathname.startsWith(to)
                    ? "bg-primary/20 text-primary"
                    : "text-foreground/80 hover:bg-secondary/15"
                }`}
                data-testid={`mobile-nav-${label.toLowerCase().replace(' ', '-')}-link`}
              >
                <Icon size={14} strokeWidth={1.5} />
                {label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      <div className="max-w-[1600px] mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8 pb-24 lg:pb-8">
        <aside className="hidden lg:block lg:col-span-3">
          <div className="sticky top-24 parchment p-6 space-y-1">
            <div className="label-mystic mb-3">Navigation</div>
            {navItems.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                  loc.pathname.startsWith(to)
                    ? "bg-primary/15 text-primary border-l-2 border-primary"
                    : "hover:bg-secondary/5 text-foreground/85"
                }`}
                data-testid={`sidebar-${label.toLowerCase().replace(' ','-')}-link`}
              >
                <Icon size={16} strokeWidth={1.25} />
                <span className="text-sm">{label}</span>
              </Link>
            ))}
            <div className="gold-divider my-4" />
            <div className="label-mystic">Cauldron Reward Token</div>
            <div className="font-serif text-4xl text-secondary mt-1" data-testid="sidebar-caul-balance">
              {wallet?.balance ?? 0}
              <span className="text-xs label-mystic ml-2">CAUL</span>
            </div>
            <Link to="/wallet" className="text-xs label-mystic hover:text-secondary mt-2 inline-block" data-testid="sidebar-view-wallet">View Wallet →</Link>
          </div>
        </aside>

        <main className="col-span-1 lg:col-span-6">{children}</main>

        <aside className="hidden lg:block lg:col-span-3">
          <div className="sticky top-24 space-y-6">
            {rightRail || <DefaultRightRail />}
          </div>
        </aside>
      </div>

      <BottomNav />
      <MobileLockupPill />
      <SiteFooter />
      <OnboardingTour />
    </div>
  );
}

export function SiteFooter({ withBottomNavOffset = true }) {
  return (
    <footer
      className="border-t border-secondary/30 mt-12 py-10 px-6 text-center bg-background/60 backdrop-blur-sm"
      style={{ paddingBottom: withBottomNavOffset ? "calc(env(safe-area-inset-bottom, 0px) + 80px)" : "calc(env(safe-area-inset-bottom, 0px) + 32px)" }}
      data-testid="site-footer"
    >
      <div className="max-w-4xl mx-auto space-y-3 leading-relaxed">
        <div className="label-mystic text-secondary" style={{ letterSpacing: "0.32em" }}>
          ⚱ Cauldron ⚱
        </div>
        <div className="font-serif text-sm tracking-wide text-foreground/90" data-testid="site-footer-copyright">
          © {new Date().getFullYear()} Cauldron Technologies, Inc. · All rights reserved.
        </div>
        <div className="text-[11px] text-foreground/55 max-w-md mx-auto leading-relaxed">
          Cauldron is a marketplace for spiritual practitioners. Services offered by
          independent practitioners are for entertainment and personal reflection;
          they are not a substitute for medical, legal, or financial advice.
        </div>
        <div className="flex justify-center gap-4 text-[10px] uppercase tracking-[0.22em] text-foreground/60 pt-2 flex-wrap">
          <a href="/faq" className="hover:text-secondary" data-testid="footer-faq-link">FAQ</a>
          <span>·</span>
          <a href="/terms" className="hover:text-secondary" data-testid="footer-terms-link">Terms</a>
          <span>·</span>
          <a href="/privacy" className="hover:text-secondary" data-testid="footer-privacy-link">Privacy</a>
          <span>·</span>
          <a href="/risk-disclosures" className="hover:text-secondary" data-testid="footer-risk-disclosures-link">Risk Disclosures</a>
          <span>·</span>
          <a href="/community-guidelines" className="hover:text-secondary" data-testid="footer-community-guidelines-link">Community Guidelines</a>
          <span>·</span>
          <a href="/elders" className="hover:text-secondary" data-testid="footer-elders-link">Elders</a>
          <span>·</span>
          <a href="/ambassadors" className="hover:text-secondary" data-testid="footer-ambassadors-link">Ambassadors</a>
          <span>·</span>
          <a href="/pricing" className="hover:text-secondary" data-testid="footer-pricing-link">Pricing</a>
        </div>
      </div>
    </footer>
  );
}

function DefaultRightRail() {
  return (
    <div className="parchment p-6">
      <div className="label-mystic mb-3">Sacred Practice</div>
      <p className="font-serif text-xl leading-snug text-foreground/90 italic">
        "As above, so below. As within, so without."
      </p>
      <div className="gold-divider my-4" />
      <div className="label-mystic mb-2">Trending</div>
      <ul className="text-sm space-y-2 text-foreground/80">
        <li># Ifá Divination</li>
        <li># New Moon Rituals</li>
        <li># Tarot Readings</li>
        <li># Hoodoo</li>
        <li># Astrology</li>
      </ul>
    </div>
  );
}
